import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import type { ItemSpec } from '../../../engine';
import {
  FIXTURE_PRICE,
  LONG_NIGHT,
  SUPERSTITION_INFO,
  buyBackPawn,
  buyBargain,
  buyFixture,
  buyLot,
  buyOffer,
  closeLot,
  curioSlots,
  hireCost,
  hirePawn,
  interestOn,
  leaveNightCounter,
  lotShape,
  mantelSlots,
  meltCard,
  meltCost,
  pickFromLot,
  priceAt,
  reorderMantel,
  ringBell,
  ringCost,
  sellItem,
  sellPawn,
  type NightRun,
  type Offer,
  type Profile,
} from '../../../campaign';
import { t } from '../../../i18n';
import { itemName, itemText } from '../../../app/describe';
import { guideSeen, markGuideSeen } from '../../../app/guides';
import { sounds } from '../../../app/sound';
import { LOT_TINTS, getScene, patchScene, type Scene } from '../../scene';
import { CardView } from '../CardView';
import { Modal } from '../Modal';
import { PawnFigure } from '../PawnFigure';
import { BenchDialog, PendingPicker, blocked, useTaker, type Apply } from '../NightCounter';
import { DeckPicker, affords, bargainPawns } from '../NightParts';
import { BrokerHand, BrokerLine, Candle, brokerSpeaks, lineFor } from './Broker';
import { Emblem } from './Emblem';
import { BrassHud, NightLabel } from './Hud';
import { InventorySheet, pawnLabel } from './Inventory';
import { ItemSheet, bargainObject, showFixture, showLot, showMantelItem, showOffer } from './Item';
import { Mantel } from './Mantel';
import { NightObject } from './Objects';
import { probeBestChain } from './probe';
import { Good } from './Velvet';
import { nightLabel, tableKindName } from './runText';

/**
 * The Counter (docs/LONG_NIGHT_ART_DIRECTION.md §11.9–§11.12): a still life under the lamp. The
 * velvet tray is the focal point, its goods standing upright with paper tickets hanging from them
 * on string; the parcels and the dome sit just below, half lit; the Back Room, the Workbench and
 * the shelf are quiet doorways along the bottom, in shadow until touched. Your brass is weighed on
 * his scale; his hands rest on the far edge of the counter. The layout never moves, so it can be
 * learned; only the light changes.
 */

type Considered =
  | { readonly kind: 'tray'; readonly i: number }
  | { readonly kind: 'lot'; readonly i: number }
  | { readonly kind: 'fixture' };

interface Coin {
  readonly key: number;
  readonly x: number;
  readonly y: number;
  readonly dx: number;
  readonly dy: number;
  readonly delay: number;
}

const isRare = (o: Offer): boolean =>
  o.kind === 'superstition' &&
  ['rare', 'legendary'].includes(
    SUPERSTITION_INFO[o.id as keyof typeof SUPERSTITION_INFO]?.rarity ?? '',
  );

export function CounterScene({
  profile,
  run,
  apply,
  reduceMotion,
  onAbandon,
}: {
  profile: Profile;
  run: NightRun;
  apply: Apply;
  reduceMotion: boolean;
  onAbandon: () => void;
}) {
  const shop = run.shop!;
  const { pending, setPending, take } = useTaker(apply);
  const [considered, setConsidered] = useState<Considered | null>(null);
  const [door, setDoor] = useState<'back' | 'bench' | 'shelf' | null>(null);
  const [inventory, setInventory] = useState(false);
  const [bench, setBench] = useState<ItemSpec | null>(null);
  const [ringing, setRinging] = useState(false);
  const [restock, setRestock] = useState(0);
  const [flying, setFlying] = useState<number | null>(null);
  const [coins, setCoins] = useState<Coin[]>([]);
  const [tipping, setTipping] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const scaleRef = useRef<HTMLDivElement>(null);
  // He speaks rarely (§18.7); his first Counter always has its line.
  const [line, setLine] = useState<string | null>(() => {
    if (!guideSeen('broker-counter')) return lineFor('firstCounter');
    if (!brokerSpeaks('counter')) return null;
    return shop.tray.some((x) => isRare(x.offer))
      ? lineFor('rare')
      : lineFor('counter', run.number + run.night + run.table);
  });
  useEffect(() => markGuideSeen('broker-counter'), []);
  const probe = useMemo(() => probeBestChain(run.lastMatch), [run.lastMatch]);
  const ring = ringCost(run);
  const fixturePrice = priceAt(run, FIXTURE_PRICE, shop.coupon);
  // Names on the tray and the parcels: two alike are told apart by their place ("2 of 2").
  const trayNames = shop.tray.map((x) => showOffer(x.offer).name);
  const lotNames = shop.lots.map((l) => itemName(`lot.${l.kind}`));
  const last = run.outcomes[run.outcomes.length - 1];
  const goodSize = shop.tray.length >= 5 ? 58 : shop.tray.length === 4 ? 66 : 76;

  /** Tokens fly in arcs from the scale to what is bought, a clink a step higher each. */
  const pay = (to: Element | null, n: number) => {
    const from = scaleRef.current;
    if (!from || !to || reduceMotion) return;
    const a = from.getBoundingClientRect();
    const b = to.getBoundingClientRect();
    const x = a.left + a.width / 2;
    const y = a.top + a.height / 2;
    const now = performance.now();
    const k = Math.min(6, Math.max(2, Math.ceil(n / 2)));
    setCoins((c) => [
      ...c,
      ...Array.from({ length: k }, (_, i) => ({
        key: now + i,
        x,
        y,
        dx: b.left + b.width / 2 - x,
        dy: b.top + b.height / 2 - y,
        delay: i * 40,
      })),
    ]);
    for (let i = 0; i < k; i++) setTimeout(() => sounds.clink(i), i * 40 + 300);
  };

  const buyTray = (i: number) => {
    const x = shop.tray[i]!;
    const el = document.querySelector(`[data-good="${i}"]`);
    const go = () => take(x.offer, (target) => (p) => buyOffer(p, i, target));
    if (reduceMotion) return go();
    // The ticket's stub tears; the tokens fly; the good lifts and goes to its place.
    sounds.play('tear');
    pay(el, x.price);
    setFlying(i);
    setTimeout(() => {
      setFlying(null);
      go();
    }, 520);
  };

  const ringTheBell = () => {
    if (!affords(run, ring)) return;
    sounds.bell(shop.rings);
    if (reduceMotion) {
      apply(ringBell);
      return;
    }
    // The bell swings; his hand sweeps the tray clear; a hush; new goods slide on.
    setRinging(true);
    setTimeout(() => {
      apply(ringBell);
      setRestock((k) => k + 1);
      setRinging(false);
      if (shop.rings + 1 === 3) setLine(lineFor('ringThrice'));
    }, 900);
  };

  const sell = (uid: string) => {
    sounds.play('creak');
    setTipping(true);
    setTimeout(() => setTipping(false), 900);
    apply((p) => sellItem(p, uid));
  };

  const leave = () => {
    if (reduceMotion) return apply(leaveNightCounter);
    // The lamp narrows toward the door, then through the dark.
    if (run.brass === 0) setLine(lineFor('leaveEmpty'));
    setLeaving(true);
    patchScene({ narrow: true, focus: 0.92 });
    setTimeout(() => apply(leaveNightCounter), run.brass === 0 ? 2200 : 650);
  };

  const c = considered;
  const consideredOffer = c?.kind === 'tray' ? shop.tray[c.i] : undefined;

  return (
    <div className={`ln-screen counter-room has-dock ${leaving ? 'is-leaving' : ''}`}>
      <h1 className="sr-only">{t('night.counter')}</h1>
      <header className="nb-head">
        <NightLabel night={run.night}>
          {nightLabel(run.night)}
          {last && !last.skipped
            ? ` · ${t('counter.after', { table: tableKindName(last.kind) })}`
            : ''}
        </NightLabel>
        <div ref={scaleRef} className={`ct-scale ${tipping ? 'is-tipping' : ''}`}>
          <BrassHud
            brass={run.brass}
            interest={interestOn(run)}
            reduceMotion={reduceMotion}
            onClick={() => setInventory(true)}
          />
        </div>
      </header>

      {/* Fixtures you own stand on the shelves behind the counter: the shop remembers your run. */}
      <div className="ct-back" aria-label={run.fixtures.length ? t('night.fixtures') : undefined}>
        {run.fixtures.map((f) => (
          <Emblem key={f} name={`fixture.${f}`} size={20} title={itemName(`fixture.${f}`)} />
        ))}
      </div>

      <div className="ct-kit">
        <Mantel
          items={run.mantel}
          slots={mantelSlots(run)}
          size="counter"
          objectOf={(item) => showMantelItem(run, item).object!}
          onReorder={(uids) => apply((p) => reorderMantel(p, uids))}
          onOpen={() => setInventory(true)}
        />
        <span
          className="ct-pockets"
          aria-label={t('night.curios', { n: run.curios.length, slots: curioSlots(run) })}
        >
          {Array.from({ length: curioSlots(run) }, (_, i) => {
            const cu = run.curios[i];
            return cu ? (
              <button
                key={cu.uid}
                type="button"
                className="ct-pocket"
                onClick={() => setInventory(true)}
                title={itemName(cu.id)}
              >
                <NightObject spec={{ kind: 'curio', emblem: cu.id }} size={34} />
              </button>
            ) : (
              <span key={`p${i}`} className="ct-pocket is-empty" />
            );
          })}
        </span>
      </div>

      {/* The tray, under the lamp: the focal point. */}
      <div className={`ct-tray ${ringing ? 'is-sweeping' : ''}`}>
        <BrokerHand side="left" pose="rest" at={0.07} still={reduceMotion} />
        <BrokerHand side="right" pose="rest" at={0.93} still={reduceMotion} />
        {/* What he says, between his hands, on a line of its own: it never covers anything. */}
        <div className="ct-says">
          {line && (
            <BrokerLine text={line} still={reduceMotion} inline onDone={() => setLine(null)} />
          )}
        </div>
        <div
          key={restock}
          className="ct-goods"
          style={{ '--good': `${goodSize}px` } as CSSProperties}
        >
          {shop.tray.map((x, i) => (
            <button
              key={`${restock}-${i}`}
              type="button"
              data-good={i}
              className={`ct-good ${x.sold ? 'is-sold' : ''} ${isRare(x.offer) ? 'is-rare' : ''} ${
                c?.kind === 'tray' && c.i === i ? 'is-considered' : ''
              } ${flying === i ? 'is-flying' : ''}`}
              style={{ '--d': i } as CSSProperties}
              disabled={x.sold}
              onClick={() => setConsidered({ kind: 'tray', i })}
              aria-label={goodLabel(showOffer(x.offer), x.price, dupe(trayNames, i))}
              title={summary(showOffer(x.offer))}
            >
              {!x.sold && (
                <>
                  <span className="ct-object">
                    <Good offer={x.offer} size={goodSize} />
                  </span>
                  <span className="ct-string" aria-hidden="true" />
                  <span
                    className={`ct-ticket ${affords(run, x.price) ? '' : 'is-short'}`}
                    aria-hidden="true"
                  >
                    <span className="ct-ticket-name">{showOffer(x.offer).name}</span>
                    {x.price === 0 ? t('night.free') : `¤${x.price}`}
                  </span>
                </>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* The parcels and the dome, half lit. */}
      <div className="ct-parcels">
        {shop.lots.map((l, i) =>
          l.sold ? (
            <span key={i} className="ct-parcel is-sold" />
          ) : (
            <button
              key={i}
              type="button"
              className={`ct-parcel ${c?.kind === 'lot' && c.i === i ? 'is-considered' : ''}`}
              onClick={() => setConsidered({ kind: 'lot', i })}
              aria-label={goodLabel(
                { name: itemName(`lot.${l.kind}`), text: itemText(`lot.${l.kind}`) },
                l.price,
                dupe(lotNames, i),
              )}
              title={itemText(`lot.${l.kind}`)}
            >
              <NightObject spec={{ kind: 'lot', lot: l.kind, size: l.size }} size={64} />
              <span
                className={`ct-ticket ${affords(run, l.price) ? '' : 'is-short'}`}
                aria-hidden="true"
              >
                <span className="ct-ticket-name">{itemName(`lot.${l.kind}`)}</span>
                {l.price === 0 ? t('night.free') : `¤${l.price}`}
              </span>
            </button>
          ),
        )}
        {run.nightFixture && (
          <button
            type="button"
            className={`ct-parcel ct-dome ${c?.kind === 'fixture' ? 'is-considered' : ''}`}
            onClick={() => setConsidered({ kind: 'fixture' })}
            aria-label={goodLabel(showFixture(run.nightFixture), fixturePrice, '')}
            title={summary(showFixture(run.nightFixture))}
          >
            <NightObject
              spec={{ kind: 'fixture', emblem: `fixture.${run.nightFixture}` }}
              size={58}
            />
            <span
              className={`ct-ticket ${affords(run, fixturePrice) ? '' : 'is-short'}`}
              aria-hidden="true"
            >
              <span className="ct-ticket-name">{itemName(`fixture.${run.nightFixture}`)}</span>¤
              {fixturePrice}
            </span>
          </button>
        )}
      </div>

      {/* The doorways, quiet in the shadow; and the bell. */}
      <nav className="ct-doors">
        {shop.backRoom && (
          <button
            type="button"
            className={`ct-door is-curtain ${run.night >= 2 ? 'stirs' : ''}`}
            onClick={() => setDoor('back')}
          >
            <span className="ct-curtain" aria-hidden="true" />
            {t('night.backRoom')}
          </button>
        )}
        <button type="button" className="ct-door" onClick={() => setDoor('bench')}>
          <Emblem name="fixture.carvers-bench" size={22} />
          {t('counter.workbench')}
        </button>
        <button type="button" className="ct-door" onClick={() => setDoor('shelf')}>
          <Emblem name="fixture.wall-shelf" size={22} />
          {t('counter.shelf')}
        </button>
      </nav>

      <div className="ln-dock">
        <button
          type="button"
          className={`ct-bell ${ringing ? 'is-ringing' : ''}`}
          disabled={!affords(run, ring) || ringing || leaving}
          onClick={ringTheBell}
          aria-label={t('night.ringShort', { n: ring })}
          title={t('night.ringHint')}
        >
          <Emblem name="fixture.shop-bell" size={30} />
          <span key={ring} className="ct-ticket is-small">
            ¤{ring}
          </span>
        </button>
        <button
          type="button"
          className="btn btn-primary btn-large ln-dock-main"
          disabled={!!shop.open || leaving}
          onClick={leave}
        >
          {t('night.leave')}
        </button>
      </div>

      {/* Tokens in flight, from the scale to what is bought. */}
      <div className="ct-coins" aria-hidden="true">
        {coins.map((k) => (
          <i
            key={k.key}
            style={
              {
                left: k.x,
                top: k.y,
                '--dx': `${k.dx}px`,
                '--dy': `${k.dy}px`,
                animationDelay: `${k.delay}ms`,
              } as CSSProperties
            }
            onAnimationEnd={() => setCoins((all) => all.filter((x) => x.key !== k.key))}
          />
        ))}
      </div>

      {/* Considering a good: its label unfolds, with what it would have added. */}
      {consideredOffer && c?.kind === 'tray' && (
        <ItemSheet
          item={showOffer(consideredOffer.offer)}
          price={consideredOffer.price}
          note={blocked(run, consideredOffer.offer, consideredOffer.price)}
          actions={[
            {
              label: consideredOffer.price
                ? t('night.buyFor', { n: consideredOffer.price })
                : t('night.take'),
              primary: true,
              disabled: blocked(run, consideredOffer.offer, consideredOffer.price) !== null,
              onClick: () => {
                setConsidered(null);
                buyTray(c.i);
              },
            },
          ]}
          onClose={() => setConsidered(null)}
        >
          <Considering
            run={run}
            offer={consideredOffer.offer}
            price={consideredOffer.price}
            probe={probe}
          />
        </ItemSheet>
      )}
      {c?.kind === 'lot' && shop.lots[c.i] && !shop.lots[c.i]!.sold && (
        <ItemSheet
          item={showLot(
            shop.lots[c.i]!,
            lotShape(shop.lots[c.i]!.kind, shop.lots[c.i]!.size).show,
            lotShape(shop.lots[c.i]!.kind, shop.lots[c.i]!.size).take,
          )}
          price={shop.lots[c.i]!.price}
          note={affords(run, shop.lots[c.i]!.price) ? null : t('night.why.brass')}
          actions={[
            {
              label: shop.lots[c.i]!.price
                ? t('night.openFor', { n: shop.lots[c.i]!.price })
                : t('night.open'),
              primary: true,
              disabled: !affords(run, shop.lots[c.i]!.price),
              onClick: () => {
                const i = c.i;
                setConsidered(null);
                apply((p) => buyLot(p, i));
              },
            },
          ]}
          onClose={() => setConsidered(null)}
        >
          <InterestDelta run={run} price={shop.lots[c.i]!.price} />
        </ItemSheet>
      )}
      {c?.kind === 'fixture' && run.nightFixture && (
        <ItemSheet
          item={showFixture(run.nightFixture)}
          price={fixturePrice}
          note={affords(run, fixturePrice) ? null : t('night.why.brass')}
          actions={[
            {
              label: t('night.buyFor', { n: fixturePrice }),
              primary: true,
              disabled: !affords(run, fixturePrice),
              onClick: () => {
                setConsidered(null);
                // The dome lifts with a ring of glass.
                sounds.play('glass');
                apply(buyFixture);
              },
            },
          ]}
          onClose={() => setConsidered(null)}
        >
          <InterestDelta run={run} price={fixturePrice} />
        </ItemSheet>
      )}

      {shop.open && (
        <LotOpening
          run={run}
          reduceMotion={reduceMotion}
          onTake={(i) => {
            const o = shop.open!.contents[i]!;
            take(o, (target) => (p) => pickFromLot(p, i, target));
          }}
          onLeave={() => apply(closeLot)}
        />
      )}
      {door === 'back' && shop.backRoom && (
        <BackRoom
          run={run}
          reduceMotion={reduceMotion}
          onStrike={() => {
            const id = shop.backRoom!.bargain;
            setDoor(null);
            setPending({
              offer: { kind: 'bargain', id },
              payPawns: bargainPawns(id),
              take: (target, payWith) => (p) => buyBargain(p, payWith, target),
            });
          }}
          onClose={() => setDoor(null)}
        />
      )}
      {door === 'bench' && (
        <Workbench
          run={run}
          apply={apply}
          reduceMotion={reduceMotion}
          onClose={() => setDoor(null)}
        />
      )}
      {door === 'shelf' && (
        <Shelf
          profile={profile}
          run={run}
          apply={apply}
          reduceMotion={reduceMotion}
          onLine={(m) => setLine(lineFor(m))}
          onClose={() => setDoor(null)}
        />
      )}
      {inventory && (
        <InventorySheet
          profile={profile}
          run={run}
          onReorder={(uids) => apply((p) => reorderMantel(p, uids))}
          onSell={(uid) => {
            setInventory(false);
            sell(uid);
          }}
          onBench={(cu) => {
            setInventory(false);
            setBench(cu);
          }}
          onAbandon={() => {
            setInventory(false);
            onAbandon();
          }}
          onClose={() => setInventory(false)}
        />
      )}
      <PendingPicker
        profile={profile}
        run={run}
        pending={pending}
        apply={apply}
        onClose={() => setPending(null)}
      />
      {bench && <BenchDialog run={run} item={bench} apply={apply} onClose={() => setBench(null)} />}
    </div>
  );
}

/** The interest buying this would cost you at the next table ("+4 → +3"). */
function InterestDelta({ run, price }: { run: NightRun; price: number }) {
  const now = interestOn(run);
  const after = interestOn({ ...run, brass: run.brass - price });
  if (now === after) return null;
  return (
    <p className="ct-delta">
      {t('counter.interest')} <b>+{now}</b> → <b>+{after}</b>
    </p>
  );
}

/**
 * What a good would have added to your best chain at the last table (§11.9), in blue and red,
 * and the interest it would cost.
 */
function Considering({
  run,
  offer,
  price,
  probe,
}: {
  run: NightRun;
  offer: Offer;
  price: number;
  probe: ReturnType<typeof probeBestChain>;
}) {
  const after =
    probe && offer.kind === 'superstition'
      ? probe.with({
          superstition: {
            uid: 'probe',
            id: offer.id,
            ...(offer.finish ? { finish: offer.finish } : {}),
          },
        })
      : probe && offer.kind === 'appraisal'
        ? probe.with({ appraisal: offer.id })
        : null;
  return (
    <>
      {probe && after !== null && after !== probe.score && (
        <p className="ct-ribbon">
          <span>{t('counter.lastBest')}</span>
          <span className="ct-ribbon-nums">
            <b className="is-before">{probe.score}</b> →{' '}
            <b className={after > probe.score ? 'is-up' : ''}>{after}</b>
          </span>
        </p>
      )}
      <InterestDelta run={run} price={price} />
    </>
  );
}

/**
 * Opening a Lot (§11.10): the parcel comes to the middle of the tray and the smoke takes the Lot's
 * tint; a hush; the string is cut; the seal cracks; the paper opens; the contents rise and fan
 * out face up. You choose; what you leave is slid back into the dark by his hand.
 */
function LotOpening({
  run,
  reduceMotion,
  onTake,
  onLeave,
}: {
  run: NightRun;
  reduceMotion: boolean;
  onTake: (i: number) => void;
  onLeave: () => void;
}) {
  const open = run.shop!.open!;
  const [stage, setStage] = useState(reduceMotion ? 5 : 0);
  const [chosen, setChosen] = useState<number | null>(null);
  const [sliding, setSliding] = useState(false);
  useEffect(() => {
    const before: Partial<Scene> = { tint: getScene().tint };
    patchScene({ tint: LOT_TINTS[open.lot.kind] ?? null });
    if (reduceMotion) return () => patchScene(before);
    const steps: [number, () => void][] = [
      [400, () => setStage(1)],
      [
        700,
        () => {
          setStage(2);
          sounds.play('snip');
        },
      ],
      [
        950,
        () => {
          setStage(3);
          sounds.play('wax');
        },
      ],
      [
        1250,
        () => {
          setStage(4);
          sounds.play('paper');
        },
      ],
      [1500, () => setStage(5)],
    ];
    const timers = steps.map(([ms, fn]) => setTimeout(fn, ms));
    return () => {
      timers.forEach(clearTimeout);
      patchScene(before);
    };
    // The opening plays once per Lot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const o = chosen !== null ? open.contents[chosen] : undefined;
  const why = o ? blocked(run, o) : null;
  return (
    <div
      className={`lot-open stage-${stage}`}
      role="dialog"
      aria-label={itemName(`lot.${open.lot.kind}`)}
    >
      <p className="lot-title">{itemName(`lot.${open.lot.kind}`)}</p>
      {stage < 5 && (
        <div className="lot-parcel">
          <NightObject spec={{ kind: 'lot', lot: open.lot.kind, size: open.lot.size }} size={150} />
          <span className="lot-chips" aria-hidden="true">
            {Array.from({ length: 6 }, (_, i) => (
              <i key={i} style={{ '--a': `${i * 60}deg` } as CSSProperties} />
            ))}
          </span>
        </div>
      )}
      {stage >= 5 && (
        <>
          <p className="lot-pick">
            {open.picks > 1 || open.lot.size === 'grand' ? (
              <span className="lot-left">{t('counter.picksLeft', { n: open.picks })}</span>
            ) : (
              t('night.lotPick', { n: open.picks })
            )}
          </p>
          <div className={`lot-contents ${sliding ? 'is-sliding' : ''}`}>
            {open.contents.map((x, i) => (
              <button
                key={i}
                type="button"
                className="lot-item"
                style={{ '--d': i } as CSSProperties}
                onClick={() => setChosen(i)}
              >
                <Good offer={x} size={70} />
                <span className="lot-item-name">{showOffer(x).name}</span>
              </button>
            ))}
          </div>
        </>
      )}
      {sliding && <BrokerHand pose="reach" at={0.5} to={0.5} still={reduceMotion} />}
      <div className="ln-dock">
        <button
          type="button"
          className="btn btn-large ln-dock-main"
          disabled={sliding}
          onClick={() => {
            if (reduceMotion) return onLeave();
            setSliding(true);
            setTimeout(onLeave, 800);
          }}
        >
          {t('night.closeLot')}
        </button>
      </div>
      {o && chosen !== null && (
        <ItemSheet
          item={showOffer(o)}
          note={why}
          actions={[
            {
              label: t('night.take'),
              primary: true,
              disabled: why !== null,
              onClick: () => {
                const i = chosen;
                setChosen(null);
                sounds.play('place');
                onTake(i);
              },
            },
          ]}
          onClose={() => setChosen(null)}
        />
      )}
    </div>
  );
}

/**
 * The Back Room (§11.11), from Night II: the one place in the shop with no spring at all. The
 * curtain parts, a hush, the smoke turns oxblood; the lamp doesn't reach in, and a single candle
 * lights the Bargain on a small table. Its cost is your pawns.
 */
function BackRoom({
  run,
  reduceMotion,
  onStrike,
  onClose,
}: {
  run: NightRun;
  reduceMotion: boolean;
  onStrike: () => void;
  onClose: () => void;
}) {
  const room = run.shop!.backRoom!;
  const [line, setLine] = useState<string | null>(() => lineFor('backRoom'));
  const [inside, setInside] = useState(reduceMotion);
  useEffect(() => {
    const s = getScene();
    const before: Partial<Scene> = {
      smoke: s.smoke,
      lamp: s.lamp,
      narrow: s.narrow,
      speed: s.speed,
    };
    sounds.play('curtain');
    sounds.hush(true);
    patchScene({ smoke: 'backRoom', lamp: 0.25, narrow: true, speed: 1 });
    const timer = setTimeout(() => setInside(true), reduceMotion ? 0 : 1500);
    return () => {
      clearTimeout(timer);
      sounds.hush(false);
      patchScene(before);
    };
  }, [reduceMotion]);
  return (
    <div
      className={`back-room ${inside ? 'is-inside' : ''}`}
      role="dialog"
      aria-label={t('night.backRoom')}
    >
      <span className="br-curtain is-left" aria-hidden="true" />
      <span className="br-curtain is-right" aria-hidden="true" />
      <div className="br-table">
        <Candle night={1} size={40} />
        {room.sold ? (
          <p className="br-empty">{t('counter.backRoomEmpty')}</p>
        ) : (
          <>
            <NightObject spec={bargainObject(room.bargain)} size={110} />
            {/* Its price: your own pieces, never tokens. */}
            {bargainPawns(room.bargain) > 0 && (
              <span
                className="br-price"
                aria-label={t('night.pawnCost', { n: bargainPawns(room.bargain) })}
              >
                {Array.from({ length: bargainPawns(room.bargain) }, (_, i) => (
                  <i key={i} />
                ))}
              </span>
            )}
            <p className="br-name">{itemName(`bargain.${room.bargain}`)}</p>
            <p className="br-text">{showOffer({ kind: 'bargain', id: room.bargain }).text}</p>
          </>
        )}
      </div>
      {line && <BrokerLine text={line} still={reduceMotion} onDone={() => setLine(null)} />}
      <div className="ln-dock">
        <button type="button" className="btn btn-large" onClick={onClose}>
          {t('counter.backOut')}
        </button>
        {!room.sold && (
          <button
            type="button"
            className="btn btn-danger btn-large ln-dock-main"
            onClick={onStrike}
          >
            {t('night.strike')}
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * The Workbench (§11.12): melting a card. The card lies on the bench; on confirming, an ember line
 * runs round its edge and burns inward, and what is left is ash that drifts up into the smoke.
 */
function Workbench({
  run,
  apply,
  reduceMotion,
  onClose,
}: {
  run: NightRun;
  apply: Apply;
  reduceMotion: boolean;
  onClose: () => void;
}) {
  const [pick, setPick] = useState<number[]>([]);
  const [burning, setBurning] = useState(false);
  const cost = meltCost(run);
  const card = pick.length === 1 ? run.deck[pick[0]!] : undefined;
  return (
    <Modal
      title={t('counter.workbench')}
      onClose={onClose}
      wide
      className="ln-sheet workbench"
      footer={
        <button
          type="button"
          className="btn btn-danger"
          disabled={!card || !affords(run, cost) || burning}
          onClick={() => {
            const i = pick[0]!;
            sounds.play('ember');
            if (reduceMotion) {
              setPick([]);
              apply((p) => meltCard(p, i));
              return;
            }
            setBurning(true);
            setTimeout(() => {
              setBurning(false);
              setPick([]);
              apply((p) => meltCard(p, i));
            }, 1300);
          }}
        >
          {t('night.meltCard', { n: cost })}
        </button>
      }
    >
      <p className="ln-hint">{t('night.meltHint')}</p>
      <div className="wb-bench">
        {card ? (
          <span className={`wb-card ${burning ? 'is-burning' : ''}`}>
            <CardView
              size="md"
              face={card.kind === 'normal' ? card.face : null}
              wild={card.kind === 'wild'}
              stamp={card.stamp}
              finish={card.finish}
              trick={card.trick}
              label=""
            />
            {burning && <span className="wb-ash" aria-hidden="true" />}
          </span>
        ) : (
          <span className="wb-empty">{t('counter.benchEmpty')}</span>
        )}
      </div>
      <DeckPicker deck={run.deck} max={1} selected={pick} onChange={setPick} />
    </Modal>
  );
}

/**
 * The shelf (§11.12): hiring a pawn (it rises out of the smoke, and its name is written on its
 * tag letter by letter), buying one back from his shelf (its shard smooths into an orb and a gold
 * seam draws itself across it), and selling one.
 */
function Shelf({
  profile,
  run,
  apply,
  reduceMotion,
  onLine,
  onClose,
}: {
  profile: Profile;
  run: NightRun;
  apply: Apply;
  reduceMotion: boolean;
  onLine: (m: 'sellPawn' | 'buyBack') => void;
  onClose: () => void;
}) {
  const shop = run.shop!;
  const hire = priceAt(run, hireCost(run));
  const back = priceAt(run, LONG_NIGHT.PAWN_BUYBACK);
  const [fresh, setFresh] = useState<string | null>(null);
  const [chosen, setChosen] = useState<string | null>(null);
  const [returned, setReturned] = useState<string | null>(null);
  const before = useRef(run.roster.map((r) => r.id));
  // A pawn that has just joined: it rises and its tag is written.
  useEffect(() => {
    const added = run.roster.find((r) => !before.current.includes(r.id));
    before.current = run.roster.map((r) => r.id);
    if (!added) return;
    if (returned === 'pending') setReturned(added.id);
    else setFresh(added.id);
  }, [run.roster, returned]);
  return (
    <Modal title={t('counter.shelf')} onClose={onClose} wide className="ln-sheet shelf">
      <p className="ln-hint">{t('night.pawnsHint')}</p>
      <div className="sh-row">
        {run.roster.map((r) => {
          const named = !r.standIn;
          const canSell = named && run.roster.length > 6;
          return (
            <button
              key={r.id}
              type="button"
              className={`sh-pawn ${r.standIn ? 'is-stand-in' : ''} ${chosen === r.id ? 'is-chosen' : ''} ${
                fresh === r.id && !reduceMotion ? 'is-fresh' : ''
              } ${returned === r.id && !reduceMotion ? 'is-returned' : ''}`}
              disabled={!canSell}
              aria-pressed={chosen === r.id}
              onClick={() => setChosen(chosen === r.id ? null : r.id)}
            >
              <span className="sh-figure">
                <PawnFigure side="S" crowned={false} immune={false} inscription={null} />
                {returned === r.id && <span className="sh-seam" aria-hidden="true" />}
              </span>
              {named && (
                <span className="fl-tag">{pawnLabel(profile, run, r.id).split(' ')[0]}</span>
              )}
            </button>
          );
        })}
      </div>
      <div className="sh-actions">
        {chosen && run.roster.some((r) => r.id === chosen) && (
          <button
            type="button"
            className="btn"
            onClick={() => {
              const id = chosen;
              setChosen(null);
              onLine('sellPawn');
              apply((p) => sellPawn(p, id));
            }}
          >
            {t('night.sellPawnNamed', { name: pawnLabel(profile, run, chosen).split(' ')[0]! })} · ¤
            {LONG_NIGHT.PAWN_SELL}
          </button>
        )}
        <button
          type="button"
          className="btn"
          disabled={!affords(run, hire)}
          onClick={() => {
            sounds.play('nib');
            apply(hirePawn);
          }}
        >
          {t('counter.hire', { n: hire })}
        </button>
        {shop.buyBack && (
          <button
            type="button"
            className="btn"
            disabled={!affords(run, back)}
            onClick={() => {
              onLine('buyBack');
              setReturned('pending');
              apply(buyBackPawn);
            }}
          >
            {t('night.buyBack', { name: profile.pawns[shop.buyBack]?.name ?? shop.buyBack })} · ¤
            {back}
          </button>
        )}
      </div>
    </Modal>
  );
}

/** "2 of 2" for the second of two goods alike on the counter; '' when no other shares its name. */
function dupe(names: readonly string[], i: number): string {
  const same = names.flatMap((n, k) => (n === names[i] ? [k] : []));
  return same.length > 1 ? t('item.count', { n: same.indexOf(i) + 1, of: same.length }) : '';
}

/** What a good does, in its first paragraph: its tooltip, and the end of its spoken name. */
const summary = (show: { readonly text: string }): string => show.text.split('\n\n')[0] ?? '';

/** A good read out in full: its name (and which, when two are alike), its price, what it does. */
const goodLabel = (
  show: { readonly name: string; readonly text: string },
  price: number,
  which: string,
): string =>
  [
    which ? `${show.name}, ${which}` : show.name,
    price === 0 ? t('night.free') : `¤${price}`,
    summary(show),
  ]
    .filter(Boolean)
    .join('. ');
